import { useState, useEffect, useRef, useCallback } from 'react';

// When loading stops, wait this long before actually hiding the indicator.
// If loading restarts within this window, the indicator stays visible
// (bridging the gap between rapid sequential loads).
const GAP_TOLERANCE_MS = 50;

// Default timing constants for calendar views.
// Exported so all consumers use the same values.
export const LOADING_GRACE_MS = 200;
export const LOADING_MIN_DISPLAY_MS = 400;

export default function useSmartLoading(
  isLoading,
  { graceMs = 200, minDisplayMs = 400 } = {},
) {
  // Mirror initial isLoading so a component that mounts mid-load shows the
  // indicator immediately. Without this, the effect below sees wasLoading ===
  // isLoading on first run and never schedules the show — leaving the skeleton
  // suppressed for the entire load.
  const [showLoading, setShowLoading] = useState(isLoading);

  const graceTimer = useRef(null);
  const gapTimer = useRef(null);
  const minDisplayTimer = useRef(null);
  // Seed showingSince when we start visible, otherwise hideWithMinDisplay's
  // elapsed-time math short-circuits and the skeleton gets stuck on.
  const showingSince = useRef(isLoading ? Date.now() : null);
  const isLoadingRef = useRef(isLoading);

  const clearAllTimers = useCallback(() => {
    clearTimeout(graceTimer.current);
    clearTimeout(gapTimer.current);
    clearTimeout(minDisplayTimer.current);
    graceTimer.current = null;
    gapTimer.current = null;
    minDisplayTimer.current = null;
  }, []);

  const show = useCallback(() => {
    showingSince.current = Date.now();
    setShowLoading(true);
  }, []);

  const hide = useCallback(() => {
    showingSince.current = null;
    setShowLoading(false);
  }, []);

  const hideWithMinDisplay = useCallback(() => {
    if (minDisplayMs <= 0 || !showingSince.current) {
      hide();
      return;
    }

    const elapsed = Date.now() - showingSince.current;
    const remaining = minDisplayMs - elapsed;

    if (remaining <= 0) {
      hide();
    } else {
      minDisplayTimer.current = setTimeout(() => {
        if (!isLoadingRef.current) {
          hide();
        }
      }, remaining);
    }
  }, [minDisplayMs, hide]);

  useEffect(() => {
    const wasLoading = isLoadingRef.current;
    isLoadingRef.current = isLoading;

    if (isLoading && !wasLoading) {
      // Transition: false -> true
      clearTimeout(gapTimer.current);
      gapTimer.current = null;
      clearTimeout(minDisplayTimer.current);
      minDisplayTimer.current = null;

      if (showingSince.current) {
        // Indicator is already visible, keep showing it
        return;
      }

      if (graceTimer.current) {
        // Grace timer already running (gap was bridged), let it continue
        return;
      }

      if (graceMs <= 0) {
        show();
      } else {
        graceTimer.current = setTimeout(() => {
          graceTimer.current = null;
          if (isLoadingRef.current) {
            show();
          }
        }, graceMs);
      }
    } else if (!isLoading && wasLoading) {
      // Transition: true -> false
      gapTimer.current = setTimeout(() => {
        gapTimer.current = null;

        // True pause confirmed — loading really stopped
        if (showingSince.current) {
          clearTimeout(graceTimer.current);
          graceTimer.current = null;
          hideWithMinDisplay();
        } else {
          // Never shown, cancel grace period
          clearTimeout(graceTimer.current);
          graceTimer.current = null;
        }
      }, GAP_TOLERANCE_MS);
    }
  }, [isLoading, graceMs, show, hide, hideWithMinDisplay]);

  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return showLoading;
}
